import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "https://eleven11-i6v4.onrender.com";

const CARD_BRAND_COLORS = {
  visa: "#1a1f71", mastercard: "#eb001b", amex: "#007bc1", discover: "#ff6600", default: "#374151",
};
const CARD_BRAND_LABELS = {
  visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover",
};

export default function PaymentMethodsScreen() {
  const navigate = useNavigate();
  const [cards, setCards]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [removing, setRemoving] = useState(null);
  const [error, setError]       = useState("");

  useEffect(() => { fetchCards(); }, []);

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/auth/me/cards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCards(data.cards || []);
    } catch {
      setError("Could not load payment methods.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (pmId) => {
    if (!window.confirm("Remove this card?")) return;
    setRemoving(pmId);
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/auth/me/cards/${pmId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setCards(prev => prev.filter(c => c.id !== pmId));
    } catch {
      setError("Could not remove card.");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        <div style={styles.header}>
          <button style={styles.back} onClick={() => navigate("/account")}>‹ Back</button>
          <div>
            <p style={styles.kicker}>ACCOUNT SETTINGS</p>
            <h1 style={styles.title}>Payment Methods</h1>
          </div>
        </div>

        <div style={styles.card}>
          <p style={styles.sectionLabel}>SAVED CARDS</p>

          {loading && <div style={styles.emptyState}>Loading cards...</div>}
          {!loading && error && <div style={styles.errorMsg}>{error}</div>}

          {!loading && !error && cards.length === 0 && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>💳</div>
              <p style={styles.emptyText}>No saved payment methods</p>
              <p style={styles.emptySubtext}>Cards are saved automatically when you complete a purchase.</p>
            </div>
          )}

          {!loading && cards.map((c, i) => (
            <div key={c.id} style={{ ...styles.cardRow, borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.06)" }}>
              <div style={styles.cardLeft}>
                <div style={{ ...styles.brandChip, background: CARD_BRAND_COLORS[c.brand] || CARD_BRAND_COLORS.default }}>
                  {(CARD_BRAND_LABELS[c.brand] || c.brand).slice(0, 4).toUpperCase()}
                </div>
                <div>
                  <div style={styles.cardNumber}>
                    {CARD_BRAND_LABELS[c.brand] || c.brand.charAt(0).toUpperCase() + c.brand.slice(1)} •••• {c.last4}
                  </div>
                  <div style={styles.cardExpiry}>Expires {c.expMonth}/{c.expYear}</div>
                </div>
              </div>
              <button
                style={{ ...styles.removeBtn, opacity: removing === c.id ? 0.5 : 1 }}
                onClick={() => handleRemove(c.id)}
                disabled={removing === c.id}
              >
                {removing === c.id ? "Removing..." : "Remove"}
              </button>
            </div>
          ))}
        </div>

        <div style={styles.infoNote}>
          <span style={styles.infoIcon}>🔒</span>
          <p style={styles.infoText}>
            Cards are securely stored by Stripe. ELEVEN never stores your full card number.
            New cards are saved automatically at checkout.
          </p>
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "linear-gradient(180deg, #f5f7fb 0%, #eef1f7 45%, #f7f8fb 100%)", paddingTop: 90, paddingBottom: 80, fontFamily: "-apple-system, BlinkMacSystemFont, Inter, Helvetica, Arial, sans-serif" },
  container: { width: "min(680px, calc(100% - 40px))", margin: "0 auto" },
  header: { marginBottom: 28 },
  back: { background: "none", border: "none", fontSize: 17, fontWeight: 700, color: "#0a84ff", cursor: "pointer", padding: 0, marginBottom: 16, display: "block" },
  kicker: { margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: 2.4, color: "#7b7f89" },
  title: { margin: "6px 0 0", fontSize: 42, letterSpacing: -1.5, fontWeight: 800, color: "#050505" },
  card: { borderRadius: 28, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.8)", boxShadow: "0 26px 60px rgba(24,35,60,0.1)", padding: "32px 36px", marginBottom: 20 },
  sectionLabel: { margin: "0 0 24px", fontSize: 12, fontWeight: 900, color: "#70747e", letterSpacing: 2.2 },
  cardRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0" },
  cardLeft: { display: "flex", alignItems: "center", gap: 16 },
  brandChip: { width: 52, height: 34, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 900, letterSpacing: 0.5 },
  cardNumber: { fontSize: 16, fontWeight: 700, color: "#111" },
  cardExpiry: { fontSize: 13, color: "#9ca3af", marginTop: 3 },
  removeBtn: { background: "none", border: "1px solid #fca5a5", borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 700, color: "#dc2626", cursor: "pointer" },
  emptyState: { textAlign: "center", padding: "40px 0" },
  emptyIcon: { fontSize: 44, marginBottom: 12 },
  emptyText: { fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 8px" },
  emptySubtext: { fontSize: 14, color: "#9ca3af", margin: 0, lineHeight: 1.6 },
  errorMsg: { color: "#dc2626", fontSize: 14, fontWeight: 600 },
  infoNote: { display: "flex", alignItems: "flex-start", gap: 12, padding: "18px 22px", borderRadius: 16, background: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.7)" },
  infoIcon: { fontSize: 18, flexShrink: 0 },
  infoText: { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 },
};